import { RARITY_COLORS, LEGENDARY_ABILITIES, formatStatMods } from '../../data/items';
import { ItemTooltip } from './ItemTooltip';

/**
 * A single inventory cell.
 *
 * Props:
 *  item        — item object | null
 *  index       — slot index
 *  onEquip(item, index) — fired on click (InventoryGrid decides equip vs sell vs lock)
 *  sellMode    — when true, show sell price badge + red hover tint
 *  sellPrice   — gold value to display (number | null)
 *  muted       — slot holds an item filtered out by the active type filter;
 *                render as a darkened placeholder so grid positions stay stable
 *  lockMode    — when true, clicks toggle the item's user-lock state
 *  playerLevel — meta player level; items with levelReq > playerLevel are locked
 */
export function ItemSlot({ item, index, onEquip, sellMode, sellPrice, muted, lockMode, playerLevel = 1, equippedSetCount, tooltipSide: tooltipSideProp }) {
  // Filtered-out occupied slot — show as a dim placeholder
  if (muted) {
    return <div className="item-slot item-slot--muted" />;
  }

  if (!item) {
    return <div className="item-slot item-slot--empty" />;
  }

  const color   = RARITY_COLORS[item.rarity] ?? '#9e9e9e';
  const lines   = formatStatMods(item.statMods);
  const ability = item.abilityId ? LEGENDARY_ABILITIES[item.abilityId] : null;
  // Level gate: locked when player meta-level is below the item's requirement
  const levelReq  = item.levelReq ?? 1;
  const levelLock = !sellMode && !lockMode && playerLevel < levelReq;
  // User-set protection lock
  const userLock  = !!item.locked;

  const handleClick = () => {
    if (levelLock) return; // silently block — tooltip already shows the requirement
    onEquip(item, index);
  };

  return (
    <div
      className={`item-slot item-slot--filled${sellMode ? ' item-slot--sell' : ''}${levelLock ? ' item-slot--locked' : ''}${lockMode ? ' item-slot--lockmode' : ''}${userLock ? ' item-slot--user-locked' : ''}${item.rarity === 'set' ? ' item-slot--set-glow' : ''}`}
      style={{ '--rarity-color': color }}
      onClick={handleClick}
    >
      <span className="item-slot-icon">{item.icon}</span>

      {/* Enhancement badge — glowing ✦ when item has been enhanced */}
      {item.enhancement && !sellMode && (
        <span className="item-slot-enh-badge" title={`Enhanced Lv ${item.enhancement.level}`}>✦</span>
      )}

      {/* Level-requirement lock badge */}
      {levelLock && <span className="item-slot-lock">🔒</span>}

      {/* User-set protection lock badge */}
      {userLock && !levelLock && (
        <span className="item-slot-user-lock-badge" title="Protected from selling">🔐</span>
      )}

      {/* Sell price badge — visible in sell mode (only for unlocked items) */}
      {sellMode && sellPrice != null && (
        <span className="item-slot-price">💰{sellPrice}g</span>
      )}

      <ItemTooltip
        item={item}
        color={color}
        lines={lines}
        ability={ability}
        equippedSetCount={equippedSetCount}
        levelLock={levelLock}
        levelReq={levelReq}
        userLock={userLock}
        sellPrice={sellPrice}
        lockMode={lockMode}
        tooltipSide={tooltipSideProp ?? ((index % 10) < 3 ? 'right' : 'center')}
      />
    </div>
  );
}
